import { bool, string } from 'prop-types'
import { css } from '@emotion/react'
import { useMedia } from 'use-media'
import { Button } from '@livechat/design-system-react-components'

import styles from '@/layout/category-styles'
import mediaQueries from '@/layout/media-queries'
import markerIcon from '@/assets/images/developers/marker.svg'
import { useImages } from '@/hooks/useImages'
import Sidebar from '@/components/Sidebar'
import ListHeader from '@/components/ProductList/Header'
import AppsSection from '@/components/Category/AppsSection'
import { useEnhancedList } from '@/context/EnhancedList'
import AppImage from '@/components/HomePage/Campaigns/Campaign/AppImage'
import DevelopersList from './DevelopersList'

const bannerWrapperCss = (theme) => css`
  background-color: ${theme.colors.grey};
  display: flex;
  position: relative;
  width: 100%;
  margin-bottom: 32px;
  padding: 24px 16px;
  border-radius: 13px;

  .gatsby-image-wrapper img {
    object-fit: contain !important;
  }

  ${mediaQueries.xsmallScreen(200)} {
    margin-bottom: 60px;
    padding: 48px 0 48px 32px;

    > div {
      width: 100%;
    }
  }
  ${mediaQueries.mediumScreen()} {
    padding: 48px 32px;
  }
  ${mediaQueries.largeScreen()} {
    min-height: 440px;
    padding: 48px 36px;
  }
  ${mediaQueries.xlargeScreen()} {
    flex-wrap: nowrap;
    padding: 50px 60px;
  }
`

const bannerDescriptionCss = css`
  h4 {
    margin-top: 0;
    margin-bottom: 25px;
    line-height: 40px;
    font-size: 40px;
    white-space: pre-line;

    @media (min-width: ${mediaQueries.MEDIUM}) and (max-width: ${mediaQueries.BASE_MEDIUM_SIZE +
      100}px) {
      font-size: 32px;
      line-height: 32px;
    }
  }

  p {
    margin-top: 16px;
    font-size: 20px;
    line-height: 28px;
    font-weight: 400;
    margin-bottom: 16px;

    ${mediaQueries.xsmallScreen(200)} {
      margin-top: 0;
      font-size: 20px;
      line-height: 28px;
    }
  }
`

const markerTextCss = css`
  position: absolute;
  top: 250px;
  font-size: 24px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  line-height: 51px;
  font-weight: 700;

  ${mediaQueries.xsmallScreen(-200)} {
    left: 16px;
    top: 25px;
  }
  ${mediaQueries.xsmallScreen(200)} {
    left: 32px;
    top: 48px;
  }
  ${mediaQueries.mediumScreen()} {
    left: 32px;
    top: 48px;
  }
  ${mediaQueries.largeScreen()} {
    left: 36px;
    top: 48px;
  }
  ${mediaQueries.xlargeScreen()} {
    left: 60px;
    top: 50px;
  }
`

const bannerInfo = css`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${mediaQueries.xsmallScreen(200)} {
    width: 55%;
    padding-right: 16px;
  }
  ${mediaQueries.smallScreen()} {
    width: 53%;
    padding-right: 24px;
  }
  ${mediaQueries.mediumScreen()} {
    justify-content: space-between;
    padding-right: 20px;
    width: 46%;
  }
  ${mediaQueries.largeScreen()} {
    padding-right: 40px;
  }
  ${mediaQueries.xlargeScreen()} {
    padding-right: 60px;
  }
`

const markerCss = css`
  margin-left: -13px;

  ${mediaQueries.xsmallScreen(-200)} {
    width: 250px;
    height: 51px;
  }
`

const actionsWrapperCss = css`
  width: 100%;
  margin-top: auto;
`

const DEVELOPERS_VIEW_LIST_FILTERS = 'DEVELOPERS_VIEW_LIST_FILTERS'

const Developers = ({ hideSidebar, showMobile, className }) => {
  const { sort, sortOptions, updateSort, items: devs } = useEnhancedList()
  const { omnichannelCampaignHero } = useImages()

  const sortConfig = {
    options: sortOptions,
    value: sort,
    onSortChange: updateSort,
  }

  const developersCampaign = {
    id: 'developers-banner',
    image: omnichannelCampaignHero,
  }

  const isMobile =
    useMedia({ maxWidth: mediaQueries.BASE_X_SMALL_SIZE + 200 }) || showMobile

  return (
    <div css={styles.category}>
      <div className="content">
        {!hideSidebar && <Sidebar className="sidebar" />}

        <div className="body">
          <article
            css={bannerWrapperCss}
            data-test="bannerWrapper"
            className={className}
          >
            <div css={bannerInfo} data-test="bannerInfo" className="bannerInfo">
              {isMobile && (
                <>
                  <img
                    css={markerCss}
                    src={markerIcon}
                    alt="Developer Program"
                  />
                  <AppImage
                    campaign={developersCampaign}
                    isMainCampaign
                    alt="Join Developers Console"
                  />
                </>
              )}
              <div
                css={bannerDescriptionCss}
                data-test="bannerDescription"
                className="bannerDescription"
              >
                {!isMobile && (
                  <img
                    css={markerCss}
                    src={markerIcon}
                    alt="Developer Program"
                  />
                )}
                <h4>
                  {'For\u00A0developers\nby\u00A0developers'}
                  <span css={markerTextCss} className="markerText">
                    Developer program
                  </span>
                </h4>
                <p>
                  {
                    'Build and monetize apps for 30,000 companies worldwide. We’ll\u00A0help you on the way to our Marketplace!'
                  }
                </p>
              </div>

              <div css={actionsWrapperCss}>
                <a
                  href={process.env.GATSBY_DEVELOPERS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="large"
                    kind="primary"
                    data-test="joinDeveloperConsoleButton"
                  >
                    Join Developer Console
                  </Button>
                </a>
              </div>
            </div>

            {!isMobile && (
              <AppImage
                campaign={developersCampaign}
                isMainCampaign
                alt="Join Developers Console"
              />
            )}
          </article>
          <AppsSection items={devs} listComponent={DevelopersList}>
            <ListHeader
              storageKey={DEVELOPERS_VIEW_LIST_FILTERS}
              title="Developers"
              subtitle="Power up your customer service with LiveChat integrations."
              sort={sortConfig}
              showEntireHeader
              isAllApps
            />
          </AppsSection>
        </div>
      </div>
    </div>
  )
}

Developers.propTypes = {
  hideSidebar: bool,
  showMobile: bool,
  className: string,
}

export default Developers
